
import { createContext, useContext, useEffect, useState } from "react";
import { Language, SUPPORTED_LANGUAGES, translations } from "@/i18n/translations";
import { supabase } from "@/integrations/supabase/client";
import Cookies from "js-cookie";
import { logger } from "@/lib/logger";

type LanguageContextType = {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string) => string;
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = "preferredLanguage";
const COOKIE_KEY = "preferredLanguage";
const COOKIE_EXPIRY = 365; // Cookie expires in 365 days

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>("en");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const detectLanguage = async () => {
      setIsLoading(true);
      try {
        logger.log("Detecting language...");
        
        // 1. Check cookies first (highest priority)
        const cookieLanguage = Cookies.get(COOKIE_KEY) as Language;
        logger.log("Cookie language check:", cookieLanguage, "Cookie name:", COOKIE_KEY);
        
        if (cookieLanguage && SUPPORTED_LANGUAGES.includes(cookieLanguage)) {
          logger.log("Setting language from cookie:", cookieLanguage);
          setLanguageState(cookieLanguage);
          // Also update localStorage for backward compatibility
          localStorage.setItem(STORAGE_KEY, cookieLanguage);
          setIsLoading(false);
          return;
        }
        
        // 2. Check if user is logged in and has a profile preference
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const { data: profile } = await supabase
            .from("profiles")
            .select("preferred_language")
            .eq("id", session.user.id)
            .single();
          
          if (profile?.preferred_language) {
            const profileLang = profile.preferred_language as Language;
            logger.log("Setting language from profile:", profileLang);
            setLanguageState(profileLang);
            
            // Set both cookie and localStorage
            logger.log("Setting cookie:", COOKIE_KEY, profileLang);
            Cookies.set(COOKIE_KEY, profileLang, { expires: COOKIE_EXPIRY, path: '/' });
            localStorage.setItem(STORAGE_KEY, profileLang);
            
            setIsLoading(false);
            return;
          }
        }
        
        // 3. Check localStorage next
        const storedLanguage = localStorage.getItem(STORAGE_KEY) as Language;
        logger.log("localStorage language check:", storedLanguage);
        
        if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage)) {
          logger.log("Setting language from localStorage:", storedLanguage);
          setLanguageState(storedLanguage);
          
          // Update cookie with localStorage language
          logger.log("Setting cookie from localStorage:", COOKIE_KEY, storedLanguage);
          Cookies.set(COOKIE_KEY, storedLanguage, { expires: COOKIE_EXPIRY, path: '/' });
          
          setIsLoading(false);
          return;
        }

        // 4. Check browser language settings
        const browserLanguages = navigator.languages || [navigator.language];
        logger.log("Browser languages:", browserLanguages);
        
        // Try to find the first supported language from browser preferences
        for (const browserLang of browserLanguages) {
          const langCode = browserLang.split('-')[0] as Language;
          if (SUPPORTED_LANGUAGES.includes(langCode)) {
            logger.log("Setting language from browser:", langCode);
            setLanguageState(langCode);
            
            // Update cookie and localStorage with browser language
            logger.log("Setting cookie from browser language:", COOKIE_KEY, langCode);
            Cookies.set(COOKIE_KEY, langCode, { expires: COOKIE_EXPIRY, path: '/' });
            localStorage.setItem(STORAGE_KEY, langCode);
            
            setIsLoading(false);
            return;
          }
        }

        // 5. Default to English if no matches found
        logger.log("Language defaulted to English");
        setLanguageState("en" as Language);
        
        // Set default cookie and localStorage
        logger.log("Setting default cookie:", COOKIE_KEY, "en");
        Cookies.set(COOKIE_KEY, "en", { expires: COOKIE_EXPIRY, path: '/' });
        localStorage.setItem(STORAGE_KEY, "en");
        
        setIsLoading(false);
      } catch (error) {
        logger.error("Error detecting language:", error);
        setLanguageState("en");
        setIsLoading(false);
      }
    };

    detectLanguage();
  }, []);

  const setLanguage = async (newLang: Language) => {
    logger.log("Setting language to:", newLang);
    
    // Store in localStorage for future visits
    localStorage.setItem(STORAGE_KEY, newLang);
    
    // Store in cookies for cross-page persistence
    // Make sure cookie is set for the entire site by using path: '/'
    logger.log("Setting cookie for language change:", COOKIE_KEY, newLang);
    Cookies.set(COOKIE_KEY, newLang, { expires: COOKIE_EXPIRY, path: '/' });
    logger.log("Cookie set:", COOKIE_KEY, newLang, "path: /");
    logger.log("Cookie value after setting:", Cookies.get(COOKIE_KEY));

    // If user is logged in, update their profile
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      await supabase
        .from("profiles")
        .update({ preferred_language: newLang })
        .eq("id", session.user.id);
    }
    
    setLanguageState(newLang);
  };

  const t = (key: string): string => {
    if (!key) return '';
    const keys = key.split(".");
    let value: any = translations[language];
    
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        logger.warn(`Translation missing for key: ${key} in language: ${language}`);
        break;
      }
    }
    
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {!isLoading && children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
